import { useGetOrgUserProfile } from './auth'
import {
  GetOrgBalanceSummaryData,
  GetOrgCreditAnalyticsArgs,
  GetOrgGraphArgs,
  GetOrgServerAnalyticsArgs,
  GetOrgStorageAnalyticsArgs,
  GetOrgUsageGraphArgs,
  GetOrgUserGraphArgs,
} from './inference.gen'
import { InferenceAPI, useTokenAndOrgId, hasAdminPrivileges } from './utils'
import { useQuery } from '@tanstack/react-query'

export const useGetOrgMetrics = (userId?: number) => {
  const { token, orgId } = useTokenAndOrgId()
  return useQuery({
    queryKey: ['org-metric', token, orgId, userId],
    enabled: !!token && !!orgId,
    queryFn: async () => {
      const body = userId
        ? { org_id: orgId, user_id: userId }
        : { org_id: orgId }

      const result = await InferenceAPI.getOrgMetric(body)
      return result.metric
    },
  })
}

export const useGetOrgGraph = (
  params: Pick<GetOrgGraphArgs, 'start_at' | 'end_at' | 'interval'>,
  userId?: number,
  enabled: boolean = true
) => {
  const { token, orgId } = useTokenAndOrgId()

  return useQuery({
    queryKey: ['org-graph', token, orgId, userId, params],
    enabled: !!token && !!orgId && enabled,
    queryFn: async () => {
      const body = userId
        ? { org_id: orgId, user_id: userId, ...params }
        : { org_id: orgId, ...params }

      const result = await InferenceAPI.getOrgGraph(body)
      return result.graph
    },
  })
}

export const useGetOrgUsersGraph = (params: GetOrgUserGraphArgs) => {
  const { token, orgId } = useTokenAndOrgId()
  return useQuery({
    queryKey: ['org-users-graph', token, orgId, params],
    enabled: !!token && !!orgId,
    refetchInterval: false,
    queryFn: async () => {
      const result = await InferenceAPI.getOrgUserGraph(params)
      return result.items
    },
  })
}

export const useGetOrgServerAnalytics = (params: GetOrgServerAnalyticsArgs) => {
  const { token, orgId } = useTokenAndOrgId()

  return useQuery({
    queryKey: ['org-server-analytics', token, orgId, params],
    enabled: !!token && !!orgId,
    queryFn: async () => {
      const result = await InferenceAPI.getOrgServerAnalytics(params)

      return result
    },
  })
}

export const useGetOrgStorageAnalytics = (
  params: GetOrgStorageAnalyticsArgs
) => {
  const { token, orgId } = useTokenAndOrgId()

  return useQuery({
    queryKey: ['org-storage-analytics', token, orgId, params],
    enabled: !!token && !!orgId,
    refetchInterval: false,
    queryFn: async () => {
      const result = await InferenceAPI.getOrgStorageAnalytics(params)
      return result
    },
  })
}

export const useGetOrgCreditAnalytics = (params: GetOrgCreditAnalyticsArgs) => {
  const { token, orgId } = useTokenAndOrgId()

  return useQuery({
    queryKey: ['org-credit-analytics', token, orgId, params],
    enabled: !!token && !!orgId,
    refetchInterval: false,
    queryFn: async () => {
      const result = await InferenceAPI.getOrgCreditAnalytics(params)
      return result
    },
  })
}

export interface FormattedOrgBalanceSummary extends GetOrgBalanceSummaryData {
  netBalanceCents: number
  netBalance: number
  formattedNetBalance: string
  formattedTotal: string
  formattedSpentTotal: string
  formattedTotalOrgBalance: string
  formattedTotalUserBalance: string
}

export const useGetOrgBalanceSummary = () => {
  const { token, orgId } = useTokenAndOrgId()

  return useQuery({
    queryKey: ['org-balance-summary', token, orgId],
    enabled: !!token && !!orgId,
    refetchInterval: false,
    queryFn: async (): Promise<FormattedOrgBalanceSummary> => {
      const result = await InferenceAPI.getOrgBalanceSummary()

      const data = result.summary
      const netBalanceCents = data.total - data.spent_total
      const netBalance = netBalanceCents / 100

      // Format currency values
      const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(amount)
      }

      return {
        ...data,
        netBalanceCents,
        netBalance,
        formattedNetBalance: formatCurrency(netBalance),
        formattedTotal: formatCurrency(data.total / 100),
        formattedSpentTotal: formatCurrency(data.spent_total / 100),
        formattedTotalOrgBalance: formatCurrency(
          data.breakdown.total_org_balance / 100
        ),
        formattedTotalUserBalance: formatCurrency(
          data.breakdown.total_user_balance / 100
        ),
      }
    },
  })
}

export const useGetOrgSpendRate = (userId?: number) => {
  const { token, orgId } = useTokenAndOrgId()

  const {
    data: orgUserProfile,
    isLoading,
    isError,
  } = useGetOrgUserProfile(orgId)
  const isAdmin = hasAdminPrivileges(orgUserProfile?.user_role?.role_name)

  return useQuery({
    queryKey: ['org-spend-rate', token, orgId, userId],
    enabled: !!token && !!orgId && isAdmin && !isLoading && !isError,
    queryFn: async () => {
      return await InferenceAPI.getOrgSpendRate()
    },
  })
}

export const useGetOrgUsageGraph = (
  params: GetOrgUsageGraphArgs,
  userId?: number,
  enabled: boolean = true
) => {
  const { token, orgId } = useTokenAndOrgId()

  // Make a copy of params to avoid modifying the original
  const apiParams = { ...params }

  // If start_at and end_at are the same day (within 24 hours), ensure we're only querying for that specific day
  if (apiParams.start_at && apiParams.end_at) {
    const startDay = Math.floor(apiParams.start_at / 86400)
    const endDay = Math.floor(apiParams.end_at / 86400)

    if (startDay === endDay) {
      // Ensure we're only getting data for this specific day
      console.log('Using single day range for API request')
    }
  }

  return useQuery({
    queryKey: ['org-usage-graph', token, orgId, userId, apiParams],
    enabled: !!token && !!orgId && enabled,
    queryFn: async () => {
      const body = userId
        ? { org_id: orgId, user_id: userId, ...apiParams }
        : { org_id: orgId, ...apiParams }

      return await InferenceAPI.getOrgUsageGraph(body)
    },
  })
}

export const useGetOrgUserSpendRate = (userId: number | undefined) => {
  const { token, orgId } = useTokenAndOrgId()

  const {
    data: orgUserProfile,
    isLoading,
    isError,
  } = useGetOrgUserProfile(orgId)
  const isAdmin = hasAdminPrivileges(orgUserProfile?.user_role?.role_name)

  return useQuery({
    queryKey: ['org-user-spend-rate', token, orgId, userId],
    enabled:
      !!token && !!orgId && !!userId && isAdmin && !isLoading && !isError,
    queryFn: async () => {
      const result = await InferenceAPI.getOrgUserSpendRate()
      return result
    },
  })
}
